/**
 * Soldes intermédiaires de gestion SYSCOHADA — feuille « États Financiers ».
 */
import type { Ecriture } from '@shared/types/Ecriture';
import { roundFCFA } from '@shared/utils/round';

export interface SoldesIntermediairesGestion {
  ventes_marchandises: number;
  cmvd: number;
  marge_commerciale: number;
  production: number;
  consommations_externes: number;
  valeur_ajoutee: number;
  subventions_exploitation: number;
  impots_taxes: number;
  charges_personnel: number;
  ebe: number;
  autres_produits: number;
  reprises: number;
  dap: number;
  autres_charges: number;
  resultat_exploitation: number;
  produits_financiers: number;
  charges_financieres: number;
  resultat_financier: number;
  rao: number;
  produits_hao: number;
  charges_hao: number;
  resultat_hao: number;
  resultat_avant_is: number;
  is: number;
  participation: number;
  resultat_net: number;
}

function flux(e: Ecriture): number {
  return e.credit - e.debit;
}

function charge(e: Ecriture): number {
  return e.debit - e.credit;
}

function prefix(compte: string, ...racines: string[]): boolean {
  return racines.some((r) => compte === r || compte.startsWith(r));
}

export class SigSyscohadaService {
  static calculer(ecritures: readonly Ecriture[]): SoldesIntermediairesGestion {
    let ventes_marchandises = 0;
    let cmvd = 0;
    let production = 0;
    let consommations_externes = 0;
    let subventions_exploitation = 0;
    let impots_taxes = 0;
    let charges_personnel = 0;
    let autres_produits = 0;
    let reprises = 0;
    let dap = 0;
    let autres_charges = 0;
    let produits_financiers = 0;
    let charges_financieres = 0;
    let produits_hao = 0;
    let charges_hao = 0;
    let is = 0;
    let participation = 0;

    for (const e of ecritures) {
      const c = e.compte.trim();
      if (prefix(c, '701')) ventes_marchandises += flux(e);
      else if (prefix(c, '601', '603')) cmvd += charge(e);
      else if (prefix(c, '702', '703', '704', '705', '71', '72', '73')) production += flux(e);
      else if (prefix(c, '60', '61', '62', '63')) consommations_externes += charge(e);
      else if (prefix(c, '74')) subventions_exploitation += flux(e);
      else if (prefix(c, '64')) impots_taxes += charge(e);
      else if (prefix(c, '66')) charges_personnel += charge(e);
      else if (prefix(c, '77', '78')) reprises += flux(e);
      else if (prefix(c, '75', '79')) autres_produits += flux(e);
      else if (prefix(c, '68')) dap += charge(e);
      else if (prefix(c, '65')) autres_charges += charge(e);
      else if (prefix(c, '76')) produits_financiers += flux(e);
      else if (prefix(c, '67')) charges_financieres += charge(e);
      else if (prefix(c, '695')) is += charge(e);
      else if (prefix(c, '691', '87')) participation += charge(e);
      else if (prefix(c, '8') && !prefix(c, '87', '89')) {
        if (prefix(c, '82', '84', '86', '88')) produits_hao += flux(e);
        else charges_hao += charge(e);
      }
    }

    ventes_marchandises = roundFCFA(ventes_marchandises);
    cmvd = roundFCFA(cmvd);
    production = roundFCFA(production);
    consommations_externes = roundFCFA(consommations_externes);
    const marge_commerciale = roundFCFA(ventes_marchandises - cmvd);
    const valeur_ajoutee = roundFCFA(marge_commerciale + production - consommations_externes);
    const ebe = roundFCFA(
      valeur_ajoutee + subventions_exploitation - impots_taxes - charges_personnel,
    );
    const resultat_exploitation = roundFCFA(ebe + autres_produits + reprises - dap - autres_charges);
    const resultat_financier = roundFCFA(produits_financiers - charges_financieres);
    const rao = roundFCFA(resultat_exploitation + resultat_financier);
    const resultat_hao = roundFCFA(produits_hao - charges_hao);
    const resultat_avant_is = roundFCFA(rao + resultat_hao);
    const resultat_net = roundFCFA(resultat_avant_is - is - participation);

    return {
      ventes_marchandises,
      cmvd,
      marge_commerciale,
      production,
      consommations_externes,
      valeur_ajoutee,
      subventions_exploitation: roundFCFA(subventions_exploitation),
      impots_taxes: roundFCFA(impots_taxes),
      charges_personnel: roundFCFA(charges_personnel),
      ebe,
      autres_produits: roundFCFA(autres_produits),
      reprises: roundFCFA(reprises),
      dap: roundFCFA(dap),
      autres_charges: roundFCFA(autres_charges),
      resultat_exploitation,
      produits_financiers: roundFCFA(produits_financiers),
      charges_financieres: roundFCFA(charges_financieres),
      resultat_financier,
      rao,
      produits_hao: roundFCFA(produits_hao),
      charges_hao: roundFCFA(charges_hao),
      resultat_hao,
      resultat_avant_is,
      is: roundFCFA(is),
      participation: roundFCFA(participation),
      resultat_net,
    };
  }
}
